import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import moment from 'moment';
import { StatusCodes } from 'http-status-codes';

import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import FnbApi from '~/src/services/api/FnbApi';
import { DATE_FORMAT, FNB_STATUS, ROLES } from '~/src/utils/constants';
import FnbPendingChange from '../components/FnbPendingChange';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';
import SyncFnbOrder from '../components/SyncFnbOrder';
import RolesDependent from '~/src/components/common/RolesDependent';
import { FnbOrderGetDto, ResultDto } from 'svila-common';
import PuffLoader from 'react-spinners/PuffLoader';
import ImportResults from '~/src/scenes/imports/components/ResultDtoDisplay';
import TableWrapper from '~/src/components/common/forms/TableWrapper/TableWrapper';
import { Colors } from '~/src/scss/common/style';
import ButtonComponent from '~/src/components/common/forms/Button/ButtonComponent';

export default function FnbOrder(): JSX.Element {
  const history = useHistory();
  const { orderId } = useParams();
  const [order, setOrder] = useState<FnbOrderGetDto>();
  const [addPendingChanges, setAddPendingChanges] = useState([]);
  const [removePendingChanges, setRemovePendingChanges] = useState([]);
  const [testResults, setTestResults] = useState<ResultDto>();
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [packLoading, setPackLoading] = useState<boolean>(false);
  const [sendLoading, setSendLoading] = useState<boolean>(false);
  const [testLoading, setTestLoading] = useState<boolean>(false);

  const togglePackedOrder = async () => {
    setPackLoading(true);
    const res = await FnbApi.togglePacked(orderId);
    if (res.status === StatusCodes.OK) {
      getFnbOrder();
    }
    setPackLoading(false);
  };

  const sendOrder = async () => {
    if (order) {
      setSendLoading(true);

      const res = await FnbApi.markAsSent(order.uniqueNumber);
      if (res.status === StatusCodes.OK) {
        getFnbOrder();
      }
      setSendLoading(false);
    }
  };

  const getFnbOrder = async () => {
    setFetchLoading(true);
    const res = await FnbApi.getOne(orderId);
    if (res.status === StatusCodes.OK) {
      setOrder(res.data);
      getDiffs(res.data.id);
    }
    setFetchLoading(false);
  };

  const getDiffs = async (id) => {
    const res = await FnbApi.getDiffsForFnbOrder(id);
    if (res.status === StatusCodes.OK) {
      const add = res.data.filter((x) => x.type === 'AddToOrder');
      setAddPendingChanges(add);
      const remove = res.data.filter((x) => x.type === 'RemoveFromOrder');
      setRemovePendingChanges(remove);
    }
  };
  const handleTest = async () => {
    setTestLoading(true);
    const res = await FnbApi.testOrder(order?.uniqueNumber);
    if (res.status === StatusCodes.OK) {
      setTestResults(res.data);
    }
    setTestLoading(false);
  };

  const showPackedButton = (): JSX.Element => {
    if (!order) {
      return <>No data</>;
    }

    if (!order?.consolidated) {
      return <>Not consolidated</>;
    }

    if (order?.isPacked) {
      return <>Packed</>;
    }

    if (order.isPacked) {
      return <PrimaryButton title="Unpack" size="sm" onClick={togglePackedOrder} isSubmitting={packLoading} />;
    }

    return <PrimaryButton title="Pack" size="sm" onClick={togglePackedOrder} isSubmitting={packLoading} />;
  };

  const sentButton = () => {
    if (!order) {
      return <>No order</>;
    }

    if (!order?.consolidated) {
      return <>Not consolidated</>;
    }

    if (!order?.isPacked) {
      return <>Not packed</>;
    }

    if (order?.isSent) {
      return <>Sent</>;
    }

    if (order?.status !== 7) {
      return <>Wrong Status {FNB_STATUS[order?.status]}</>;
    }

    return <PrimaryButton title="Send" size="sm" onClick={sendOrder} isSubmitting={sendLoading} />;
  };

  useEffect(() => {
    getFnbOrder();
  }, []);

  return (
    <WhitePad isFluid>
      {order && !fetchLoading ? (
        <div>
          <TitleComponent onLeftHandler={history.goBack} title="FNB Order" />
          <RolesDependent
            roles={[
              ROLES.ADMIN,
              ROLES.SUPER_ADMIN,
              ROLES.WAREHOUSE_ADMIN,
              ROLES.WAREHOUSE,
              ROLES.WAREHOUSE_ADMIN,
              ROLES.OFFICE,
              ROLES.SALES,
              ROLES.DATABASE_ADMIN,
            ]}
          >
            <div>
              {addPendingChanges?.map((diff) => (
                <FnbPendingChange pendingChange={diff} />
              ))}
            </div>
            {removePendingChanges?.map((diff) => (
              <FnbPendingChange pendingChange={diff} />
            ))}
          </RolesDependent>

          <WrapperInfoOrder style={{ marginBottom: 15 }}>
            <div>
              {/* <Text> */}
              <Description>Статус:</Description>
              <Info>
                <span>{FNB_STATUS[order?.status]}</span>
                <div>{order?.fnbItemReservationStatus}</div>
              </Info>
            </div>
            <RolesDependent roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.DATABASE_ADMIN, ROLES.OFFICE]}>
              <div>
                <Description>Packing:</Description>
                <Info>{showPackedButton()}</Info>
              </div>
            </RolesDependent>
            <RolesDependent>
              <div>
                <Description>Sending:</Description>
                <Info>{sentButton()}</Info>
              </div>
            </RolesDependent>
            <div>{/* </Text> */}</div>
          </WrapperInfoOrder>
          <WrapperInfoOrder>
            <div>
              <Description>Име:</Description>
              <Info>
                {order?.name} {order?.nameLast}
              </Info>
            </div>
            <div>
              <Description>Телефон:</Description>
              <Info>{order?.phone}</Info>
            </div>
            <div>
              <Description>Номер на поръчка:</Description>
              <a href={`https://www.fnb.bg/cms/orders/details?Id=${order?.gombaId}#status`} target="_blank">
                <Info>{order?.uniqueNumber}</Info>
              </a>
            </div>
            <div>
              <Description>Пореден номер на поръчка:</Description>
              <Info>{order?.gombaId}</Info>
            </div>
            <div>
              <Description>Дата:</Description>
              <Info>{order ? moment(order.orderDate).format(DATE_FORMAT) : null}</Info>
            </div>
            <div>
              <Description>Обща стойност:</Description>
              <Info>{order?.total} лв.</Info>
            </div>
          </WrapperInfoOrder>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 30 }}>
            <SyncFnbOrder dataItemId={order?.id} onUpdate={getFnbOrder} />
            <RolesDependent roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}>
              <PrimaryButton title="TestOrder" onClick={handleTest} size="sm" isSubmitting={testLoading} />
              {testResults && <ImportResults result={testResults} />}
            </RolesDependent>
          </div>

          <h3 style={{ color: Colors.darkBrown }}>Продукти в поръчката</h3>

          <TableWrapper>
            <thead>
              <RowCell>
                <HeaderCell align="left">Продукти</HeaderCell>
                <HeaderCell>Код</HeaderCell>
                <HeaderCell>Брой</HeaderCell>
                <HeaderCell align="right">Unit Price</HeaderCell>
                <HeaderCell align="right">Unit Total</HeaderCell>
                <HeaderCell align="right">Problem</HeaderCell>
                <HeaderCell align="right">Reservation</HeaderCell>
              </RowCell>
            </thead>
            <tbody>
              {order?.items?.map((item) => {
                return (
                  <RowCell key={item.id}>
                    <Cell align="left">{item.name}</Cell>
                    <Cell>{item.code}</Cell>
                    <Cell>{item.qtty} бр.</Cell>
                    <Cell align="right">
                      {item.price} {item.totalPrice} лв.
                    </Cell>
                    <Cell align="right">{item.totalAmount} лв.</Cell>
                    <Cell>{item.problem}</Cell>
                    <Cell>{item.fnbItemReservationStatus}</Cell>
                  </RowCell>
                );
              })}

              {/* <RowCell>
                <Cell />
                <Cell />
                <Cell />
                <Cell />
                <Cell>
                  <CellSum>
                    <Description>Общо продукти:</Description>
                    <Info>{order?.totalBase} лв.</Info>
                  </CellSum>

                  <CellSum>
                    <Description>Доставка:</Description>
                    <Info>{order?.shipmentCost} лв.</Info>
                  </CellSum>

                  <CellSum>
                    <Description>Отстъпка:</Description>
                    <Info>-{order?.discount} лв.</Info>
                  </CellSum>

                  <CellSum>
                    <Description>Обща стойност:</Description>
                    <Info>{order?.total} лв.</Info>
                  </CellSum>
                </Cell>
              </RowCell> */}
            </tbody>
          </TableWrapper>
          <WrapperInfoTable>
            <Cell>
              <CellSum>
                <div>Общо продукти:</div>
                <div>{order?.totalBase} лв.</div>
              </CellSum>

              <CellSum>
                <div>Доставка:</div>
                <div>{order?.shipmentCost} лв.</div>
              </CellSum>

              <CellSum>
                <div>Отстъпка:</div>
                <div>-{order?.discount} лв.</div>
              </CellSum>

              <CellSum>
                <div>Обща стойност:</div>
                <div>{order?.total} лв.</div>
              </CellSum>
            </Cell>
          </WrapperInfoTable>
        </div>
      ) : (
        <PuffLoader color="#000000" loading={true} size={25} />
      )}
    </WhitePad>
  );
}

const Description = styled.div`
  font-size: 18px;
  color: #a7a7a7;
  font-weight: 700;
  margin-bottom: 8px;
  text-align: center;
`;

const Info = styled.div`
  font-size: 18px !important;
  color: ${Colors.darkBrown}!important;
  font-weight: 700;
  text-align: center;
`;

const WrapperInfoOrder = styled.div`
  display: flex;
  justify-content: space-brtween;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: #f4f4f4;
  padding: 50px 60px;
  border-radius: 10px;
  div {
    display: flex;
    flex-direction: column;
  }
`;

const WrapperInfoTable = styled.div`
  display: flex;
  justify-content: end;

  td {
    background: ${Colors.darkBrown};
    color: #fff;
    font-weight: 400 !important;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    padding: 12px 18px;
  }
`;

const Cell = styled.td`
  padding: 5px;
  font-weight: 400;
  border: 1px solid #f0f0f0;
  text-align: ${(props) => (props.align ? props.align : 'center')};
`;

const HeaderCell = styled.th`
  padding: 5px;
  border: 1px solid #f0f0f0;
  font-weight: bold;
  white-space: pre-line;
  text-align: ${(props) => (props.align ? props.align : 'center')};
`;

const RowCell = styled.tr``;

const Text = styled.p`
  margin-bottom: 5px;
  font-size: 15px;
  color: #333;

  & span {
    margin-left: 5px;
    font-weight: bold;
  }
`;

const CellSum = styled.div`
  display: flex;
  justify-content: space-between;

  & div {
    font-weight: 400;
  }
  div:first-of-type {
    margin-right: 105px;
    font-weight: 400;
  }
`;

const DropDownWrapper = styled.div`
  float: right;
`;
